import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import {
  Shuffle, Calendar, Users, ChevronRight, ArrowLeft,
  Play, Lock, Unlock, Download, RefreshCw,
  BarChart3, ArrowUpDown, User, Search,
  ArrowUp, ArrowDown, AlertTriangle, Settings2, Timer, ExternalLink, UserPlus,
  Clock, CheckCircle2, Ban, Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StatusBadge } from '@/components/ui/status-badge';
import { EmptyState } from '@/components/ui/empty-state';
import { useJackpotsList, useJackpotDetail } from '@/api/extensions/useJackpotOps';
import { useRodeosList, useRodeoDetail } from '@/api/extensions/useRodeoOps';
import RodeoDrawManagement from './RodeoDrawManagement';
import { httpClient } from '@/api/http-client';
import {
  useDrawStatus, useDrawTeams, useExecuteDraw,
  useRollbackDraw, useLockDraw, useUpdateDrawConfig,
  useSetDrawResultsVisibility,
} from '@/api/extensions/useDrawOps';
import { useEventCapacity, useEventRegistrations, useAdminPairPartnerless } from '@/api/extensions/useRegistrationOps';
import { useEvents } from '@/api/generated_hooks/generated-events-hooks';
import { useToast } from '@/contexts/ToastContext';
import {
  useEventDrawStatuses,
  type EventDrawStatus,
  type DrawPlacementStatus,
} from '@/api/extensions/useDrawListStatus';
import { formatDateRange } from '@/utils/formatters';
import { getEventDisplayTitle, isTeamRopingEvent } from '@/utils/eventTypes';
import {
  type ScheduleTab,
  isPastSchedule,
  sortScheduleByTab,
} from '@/utils/scheduleTabs';
import { createScheduleListState } from '@/utils/scheduleListState';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { AdminAddContestantDialog } from '@/components/admin/AdminAddContestantDialog';
import { RodeoLevelConflictsPanel } from '@/components/delegate/RodeoLevelConflictsPanel';
import { RodeoBuddyGroupsPanel } from '@/components/delegate/RodeoBuddyGroupsPanel';
import { RodeoBulkDraftActions } from '@/components/delegate/RodeoBulkDraftActions';
import { ManageDrawsRodeoCard } from '@/components/delegate/ManageDrawsRodeoCard';

interface RegistrationCounts {
  [eventId: string]: number;
}

// ─── Draw Management View ───────────────────────────────────────────────────

interface DrawManagementProps {
  eventId: string;
  eventName: string;
  jackpotId: string | null;
  onBack: () => void;
}

type DrawTab = 'overview' | 'teams' | 'draw-order' | 'contestants';
type ConfirmAction = 'rollback' | 'lock' | 'unlock' | null;

function DrawManagement({ eventId, eventName, jackpotId, onBack }: DrawManagementProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DrawTab>('overview');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPickRounds, setCreatePickRounds] = useState(0);
  const [createDrawRounds, setCreateDrawRounds] = useState(0);
  const [createAlgorithm, setCreateAlgorithm] = useState('random_balanced');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [modalInitialized, setModalInitialized] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [teamSortField, setTeamSortField] = useState<string>('team_number');
  const [teamSortDir, setTeamSortDir] = useState<'asc' | 'desc'>('asc');
  const [teamStatusFilter, setTeamStatusFilter] = useState('');
  const [teamTypeFilter, setTeamTypeFilter] = useState('');
  const [drawOrder, setDrawOrder] = useState<any[]>([]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [drawOrderDirty, setDrawOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useDrawStatus(eventId);
  const { data: teamsRaw, isLoading: teamsLoading, refetch: refetchTeams } = useDrawTeams(eventId);
  // Registrations are pulled so the Overview tab can surface contestants
  // that have an entry but no approved partner — these are excluded from
  // the draw (draw_eligible=false) until they invite/accept a partner, so
  // they otherwise disappear from the admin's view of the event. Without
  // this callout the admin has no way to spot an orphan + help them pair
  // up or draw them out.
  const { data: regsResp } = useEventRegistrations(eventId);
  // Discipline gate. The partnerless panel ONLY applies to team-roping;
  // every other discipline (Tie-Down, Barrel Racing, Bareback, etc.)
  // has one contestant per entry, so EVERY row is `individual` with no
  // partner linkage — without this gate the legacy-orphan branch below
  // would flag the entire field as partnerless on every non-team event.
  const { data: drawEvent } = useEvents(eventId);
  const isTeamEvent = useMemo(
    () => isTeamRopingEvent(drawEvent as any),
    [drawEvent],
  );
  const partnerlessRegs: Array<{ id: string; name: string; position: string | null; status: string | null; user_profile_id: string | null }> = useMemo(() => {
    if (!isTeamEvent) return [];
    const list: any[] = Array.isArray(regsResp)
      ? regsResp
      : (regsResp as any)?.registrations ?? (regsResp as any)?.data ?? [];
    return list
      .filter((r: any) => {
        const status = (r.trig_status_name ?? r.trig_status ?? '').toLowerCase();
        if (status === 'cancelled') return false;
        // needs_partner is the explicit flag (set when a partner declines or
        // is cancelled); the legacy individual/none combo on a team-roping
        // event also counts as an orphan even though new entries can't
        // create it any more.
        const hasPartnerLink = !!r.partner_user_profile_id || !!r.partner_registration_id;
        const hasConfirmedPick = !!r.pick_partner_id && r.pick_confirmed === true;
        if (r.partner_status === 'needs_partner') return true;
        if ((r.registration_type === 'individual' || r.registration_type === 'team_primary') &&
            !hasPartnerLink && !hasConfirmedPick &&
            (r.partner_status === 'none' || r.partner_status === 'cancelled' || r.partner_status === 'rejected')) {
          return true;
        }
        return false;
      })
      .map((r: any) => ({
        id: r.id,
        name: [r.trig_first_name ?? r.first_name, r.trig_last_name ?? r.last_name].filter(Boolean).join(' ').trim() || r.trig_user_full_name || '—',
        position: r.position ?? null,
        status: r.partner_status ?? null,
        user_profile_id: r.user_profile_id ?? null,
      }));
  }, [regsResp, isTeamEvent]);
  // capacity intentionally unused (for future UI surfacing)
  void useEventCapacity(eventId);
  const { data: jackpotDetail } = useJackpotDetail(jackpotId ?? '');

  const executeDraw = useExecuteDraw();
  const rollbackDraw = useRollbackDraw();
  const lockDraw = useLockDraw();
  const updateDrawConfig = useUpdateDrawConfig();
  const pairPartnerless = useAdminPairPartnerless();
  const { showSuccess, showError } = useToast();
  // Tracks which partnerless row the admin has opened the pair-with
  // dropdown for. Only one open at a time so the UI stays scannable.
  const [pairingOpenFor, setPairingOpenFor] = useState<string | null>(null);

  // Map API response fields
  const s = status as any;
  const isExecuted = !!s?.draw_executed;
  const isLocked = !!s?.draw_locked;
  const totalTeams = s?.total_teams ?? 0;
  const pickTeams = s?.pick_teams ?? 0;
  const drawTeams = s?.draw_teams ?? 0;
  const totalEligible = s?.total_eligible ?? 0;
  const headersCount = s?.headers_count ?? 0;
  const heelersCount = s?.heelers_count ?? 0;
  const eventTotalRounds = s?.total_rounds ?? 0;
  const eventPickCount = s?.pick_count ?? 0;
  const eventDrawCount = s?.draw_count ?? 0;
  const eventAlgorithm = s?.draw_algorithm ?? 'random_balanced';
  void s?.last_execution;

  const isPending = executeDraw.isPending || rollbackDraw.isPending || lockDraw.isPending || updateDrawConfig.isPending;

  // Reset modal state when switching events
  useEffect(() => {
    setModalInitialized(false);
  }, [eventId]);

  // Initialize modal defaults from event config when status loads
  useEffect(() => {
    if (s && !modalInitialized) {
      // Default: if pick/draw counts are set, use them; otherwise default draw_count to total_rounds
      const pc = eventPickCount > 0 ? eventPickCount : 0;
      const dc = eventDrawCount > 0 ? eventDrawCount : (eventTotalRounds > 0 ? eventTotalRounds : 1);
      setCreatePickRounds(pc);
      setCreateDrawRounds(dc);
      setCreateAlgorithm(eventAlgorithm);
      setModalInitialized(true);
    }
  }, [s, modalInitialized, eventPickCount, eventDrawCount, eventTotalRounds, eventAlgorithm]);

  // Process teams with member_names array
  const teams = useMemo(() => {
    if (!teamsRaw) return [];
    return (teamsRaw as any)?.data
      ? (teamsRaw as any).data
      : Array.isArray(teamsRaw) ? teamsRaw : [];
  }, [teamsRaw]);

  const activeTeams = teams.filter((t: any) => t.status === 'active');
  const eliminatedTeams = teams.filter((t: any) => t.status !== 'active');

  // Sync drawOrder from teams when teams change
  useEffect(() => {
    if (teams.length > 0 && !drawOrderDirty) {
      setDrawOrder([...teams].sort((a: any, b: any) => (a.current_draw_order ?? 0) - (b.current_draw_order ?? 0)));
    }
  }, [teams, drawOrderDirty]);

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const handleDrop = (dropIdx: number) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    const items = [...drawOrder];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dropIdx, 0, moved);
    items.forEach((t, i) => { t.current_draw_order = i + 1; });
    setDrawOrder(items);
    setDrawOrderDirty(true);
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const saveDrawOrder = async () => {
    setSavingOrder(true);
    try {
      // Single bulk reorder request — no rate limit issues
      await httpClient.post(`/draw-ops/event/${eventId}/teams/reorder`, {
        order: drawOrder.map((t: any) => ({ team_id: t.id, draw_order: t.current_draw_order })),
      });
      setDrawOrderDirty(false);
      refetchTeams();
    } catch (err) {
      console.error('Failed to save draw order:', err);
    } finally {
      setSavingOrder(false);
    }
  };

  // Sort and filter teams
  const sortedTeams = useMemo(() => {
    let list = [...teams];
    if (teamStatusFilter) {
      list = list.filter((t: any) => t.status === teamStatusFilter);
    }
    if (teamTypeFilter) {
      list = list.filter((t: any) => t.team_type === teamTypeFilter);
    }
    if (teamSearch) {
      const q = teamSearch.toLowerCase();
      list = list.filter((t: any) => {
        const names = t.member_names ?? [];
        return (
          names.some((n: string) => n?.toLowerCase().includes(q)) ||
          String(t.team_number).includes(q)
        );
      });
    }
    list.sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      if (teamSortField === 'team_number') {
        aVal = a.team_number ?? 0;
        bVal = b.team_number ?? 0;
      } else if (teamSortField === 'header') {
        aVal = (a.member_names ?? [])[0] ?? '';
        bVal = (b.member_names ?? [])[0] ?? '';
      } else if (teamSortField === 'heeler') {
        aVal = (a.member_names ?? [])[1] ?? '';
        bVal = (b.member_names ?? [])[1] ?? '';
      } else if (teamSortField === 'status') {
        aVal = a.status ?? '';
        bVal = b.status ?? '';
      } else {
        aVal = a[teamSortField] ?? '';
        bVal = b[teamSortField] ?? '';
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return teamSortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return teamSortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [teams, teamSearch, teamSortField, teamSortDir, teamStatusFilter, teamTypeFilter]);

  const toggleSort = (field: string) => {
    if (teamSortField === field) {
      setTeamSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setTeamSortField(field);
      setTeamSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (teamSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return teamSortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Create Teams: if already executed rollback first, then update config + execute
  const handleCreateTeams = async () => {
    try {
      // If recreating, rollback existing draw first
      if (isExecuted) {
        await rollbackDraw.mutateAsync(eventId);
      }
      // Step 1: Update draw config with pick/draw round counts + algorithm
      await updateDrawConfig.mutateAsync({
        eventId,
        pick_count: createPickRounds,
        draw_count: createDrawRounds,
        draw_algorithm: createAlgorithm,
      });
      // Step 2: Execute the draw
      await executeDraw.mutateAsync({ eventId });
      setShowCreateModal(false);
      refetchStatus();
      refetchTeams();
    } catch (err: any) {
      // Error will be shown via mutation state; keep modal open
      console.error('Create teams failed:', err);
      // Refetch status since rollback may have changed state
      refetchStatus();
    }
  };

  // Confirm dialog for non-create actions
  const handleConfirm = async () => {
    if (confirmAction === 'rollback') {
      await rollbackDraw.mutateAsync(eventId);
    } else if (confirmAction === 'lock') {
      await lockDraw.mutateAsync({ eventId, lock: true });
    } else if (confirmAction === 'unlock') {
      await lockDraw.mutateAsync({ eventId, lock: false });
    }
    setConfirmAction(null);
    refetchStatus();
    refetchTeams();
  };

  const confirmMessages: Record<string, { title: string; desc: string; destructive?: boolean }> = {
    rollback: {
      title: 'Rollback Draw',
      desc: 'This will remove all generated teams. This action cannot be easily undone.',
      destructive: true,
    },
    lock: {
      title: 'Lock Draw',
      desc: 'Locking prevents any further changes to team assignments.',
    },
    unlock: {
      title: 'Unlock Draw',
      desc: 'Unlocking allows team changes. Use carefully if scoring has started.',
    },
  };

  // Jackpot info
  const jp = jackpotDetail as any;

  if (statusLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  const tabs: { id: DrawTab; label: string; icon: any; count?: number; disabled?: boolean }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'teams', label: 'Teams', icon: Users, count: teams.length, disabled: !isExecuted },
    { id: 'draw-order', label: 'Draw Order', icon: ArrowUpDown, disabled: !isExecuted },
    { id: 'contestants', label: 'Contestants', icon: User, count: totalEligible },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="text-blue-500 hover:text-blue-400 text-sm flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Manage Draws
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Draw Management</h1>
            <p className="text-sm text-muted-foreground">{eventName}</p>
          </div>
          <div className="flex gap-2">
            {isExecuted && (
              <>
                <Button size="sm" variant="outline" onClick={() => {/* TODO: export */}}>
                  <Download className="h-4 w-4 mr-1" /> Export Teams
                </Button>
                <Button
                  size="sm"
                  className={isLocked
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                  }
                  onClick={() => setConfirmAction(isLocked ? 'unlock' : 'lock')}
                  disabled={isPending}
                >
                  {isLocked ? <><Unlock className="h-4 w-4 mr-1" /> Unlock Draw</> : <><Lock className="h-4 w-4 mr-1" /> Lock Draw</>}
                </Button>
              </>
            )}
            {!isExecuted && (
              <Button
                size="sm"
                onClick={() => setShowCreateModal(true)}
                disabled={isPending}
              >
                <Play className="h-4 w-4 mr-1" /> Create Teams
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Dashboard Header */}
      <div className="rounded-lg border bg-card p-4">
        <div className="grid grid-cols-3 md:grid-cols-5 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold">{totalTeams}</div>
            <div className="text-sm text-muted-foreground">Total Teams</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-500">{activeTeams.length}</div>
            <div className="text-sm text-muted-foreground">Active Teams</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-500">{eliminatedTeams.length}</div>
            <div className="text-sm text-muted-foreground">Eliminated Teams</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-500">{totalEligible}</div>
            <div className="text-sm text-muted-foreground">Contestants</div>
          </div>
          <div>
            <div className="text-2xl font-bold">
              <span className="text-blue-600">{headersCount}H</span>
              <span className="text-muted-foreground mx-1">/</span>
              <span className="text-purple-500">{heelersCount}He</span>
            </div>
            <div className="text-sm text-muted-foreground">Header / Heeler</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-lg border bg-card">
        <div className="border-b">
          <nav className="flex space-x-6 px-6" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-500'
                    : tab.disabled
                      ? 'border-transparent text-muted-foreground/50 cursor-not-allowed'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-400'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.count !== undefined && ` (${tab.count})`}
              </button>
            ))}
            {isExecuted && (
              <button
                onClick={() => navigate(`/delegate/live-scoring/${eventId}`)}
                className="py-3 px-1 border-b-2 border-transparent font-medium text-sm flex items-center gap-2 text-green-500 hover:text-green-400"
              >
                <Timer className="h-4 w-4" />
                Go to Live Scoring
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {/* ─── Overview Tab ─────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Needs-Partner alert — surfaces team-event contestants who
                  have an entry but no approved partner. These rows are
                  excluded from the draw (draw_eligible=false) so they would
                  otherwise be invisible on this screen; without surfacing
                  them here, the admin has no way to know they need help
                  pairing up or being drawn out. */}
              {partnerlessRegs.length > 0 && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                        {partnerlessRegs.length} entr{partnerlessRegs.length === 1 ? 'y' : 'ies'} without an approved partner
                      </h3>
                      <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5">
                        These contestants paid or registered but their entry will NOT be included in the draw until they have a partner. Pair two of them together below, or draw them out from the Contestants tab.
                      </p>
                      <ul className="mt-3 space-y-1 text-sm">
                        {partnerlessRegs.slice(0, 10).map((r) => {
                          // Candidates: any other partnerless row of the
                          // OPPOSITE position whose underlying contestant
                          // isn't the same person.
                          const candidates = partnerlessRegs.filter(
                            (other) =>
                              other.id !== r.id &&
                              !!other.position && !!r.position &&
                              other.position !== r.position &&
                              (!other.user_profile_id || !r.user_profile_id ||
                                other.user_profile_id !== r.user_profile_id),
                          );
                          const isOpen = pairingOpenFor === r.id;
                          const isBusy =
                            pairPartnerless.isPending &&
                            (pairPartnerless.variables?.primary_registration_id === r.id ||
                              pairPartnerless.variables?.partner_registration_id === r.id);
                          return (
                            <li key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-amber-900 dark:text-amber-100">
                              <span className="font-medium">{r.name}</span>
                              {r.position && (
                                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-800 dark:text-amber-200">
                                  {r.position}
                                </Badge>
                              )}
                              {r.status === 'needs_partner' && (
                                <span className="text-xs text-amber-700 dark:text-amber-300">— previous partner declined</span>
                              )}
                              {candidates.length > 0 && !isOpen && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs border-amber-400 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40 ml-auto"
                                  disabled={isBusy}
                                  onClick={() => setPairingOpenFor(r.id)}
                                >
                                  {isBusy ? 'Pairing…' : 'Pair with…'}
                                </Button>
                              )}
                              {isOpen && (
                                <div className="flex flex-wrap items-center gap-1 ml-auto">
                                  <Select
                                    onValueChange={async (partnerId) => {
                                      if (!partnerId) return;
                                      try {
                                        await pairPartnerless.mutateAsync({
                                          primary_registration_id: r.id,
                                          partner_registration_id: partnerId,
                                        });
                                        showSuccess('Team paired', `${r.name} is now in the draw.`);
                                        setPairingOpenFor(null);
                                      } catch (err: any) {
                                        showError(
                                          'Could not pair',
                                          err?.response?.data?.error ?? err?.message ?? 'Unknown error',
                                        );
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-7 text-xs w-[200px] border-amber-400 bg-white dark:bg-amber-950/40">
                                      <SelectValue placeholder="Choose partner…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {candidates.map((c) => (
                                        <SelectItem key={c.id} value={c.id} className="text-xs">
                                          {c.name}
                                          {c.position ? ` (${c.position})` : ''}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-xs text-amber-800 dark:text-amber-200"
                                    onClick={() => setPairingOpenFor(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              )}
                            </li>
                          );
                        })}
                        {partnerlessRegs.length > 10 && (
                          <li className="text-xs text-amber-700 dark:text-amber-300">
                            …and {partnerlessRegs.length - 10} more
                          </li>
                        )}
                      </ul>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 border-amber-500 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
                        onClick={() => setActiveTab('contestants')}
                      >
                        Go to Contestants tab
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Jackpot & Event Info Card */}
              <div className="bg-gray-700 dark:bg-gray-800 rounded-lg p-6 text-white">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    {jp && (
                      <div className="mb-3">
                        <p className="text-gray-400 text-xs uppercase tracking-wide">Jackpot</p>
                        <h2 className="text-xl font-bold">{jp.name}</h2>
                        {jp.primary_organization_name && (
                          <p className="text-gray-300 text-sm mt-0.5">{jp.primary_organization_name}</p>
                        )}
                      </div>
                    )}
                    <div className={jp ? 'border-t border-gray-600 pt-3' : ''}>
                      <p className="text-gray-400 text-xs uppercase tracking-wide">Event</p>
                      <h3 className="text-lg font-semibold">{eventName}</h3>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-sm">
                    {jp?.start_date && (
                      <div className="flex items-center gap-2 bg-gray-600/50 rounded-lg px-3 py-2">
                        <Calendar className="h-4 w-4 text-gray-300" />
                        <span>{formatDateRange(jp.start_date, jp.end_date)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 bg-gray-600/50 rounded-lg px-3 py-2">
                      <Users className="h-4 w-4 text-blue-400" />
                      <span>{totalEligible} Contestants</span>
                      <span className="text-gray-400">&bull;</span>
                      <span>{headersCount}H / {heelersCount}He</span>
                    </div>
                  </div>
                </div>
              </div>

              {!isExecuted ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Teams Not Created</h3>
                  <p className="text-muted-foreground mb-6">
                    Create teams to begin managing contestants for this event.
                  </p>
                  <Button onClick={() => setShowCreateModal(true)} disabled={isPending}>
                    <Play className="h-5 w-5 mr-2" /> Create Teams Now
                  </Button>
                </div>
              ) : (
                <>
                  {/* Team Breakdown */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Team Breakdown</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-muted-foreground">Pick Teams</span>
                          <span className="text-2xl font-bold">{pickTeams}</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: totalTeams > 0 ? `${(pickTeams / totalTeams) * 100}%` : '0%' }}
                          />
                        </div>
                      </div>
                      <div className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-muted-foreground">Draw Teams</span>
                          <span className="text-2xl font-bold">{drawTeams}</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full transition-all"
                            style={{ width: totalTeams > 0 ? `${(drawTeams / totalTeams) * 100}%` : '0%' }}
                          />
                        </div>
                      </div>
                      <div className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-muted-foreground">Buddy Groups</span>
                          <span className="text-2xl font-bold">{totalTeams - pickTeams - drawTeams}</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full transition-all"
                            style={{ width: totalTeams > 0 ? `${((totalTeams - pickTeams - drawTeams) / totalTeams) * 100}%` : '0%' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Draw Actions */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">Draw Actions</h3>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { refetchTeams(); refetchStatus(); }}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh Teams
                      </Button>
                      {!isLocked && (
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => setShowCreateModal(true)}
                          disabled={isPending}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" /> Recreate Teams
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmAction('rollback')}
                        disabled={isPending || isLocked}
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" /> Delete All Teams
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ─── Teams Tab ────────────────────────────────────────── */}
          {activeTab === 'teams' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">All Teams</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{sortedTeams.length} teams loaded</span>
                  <Button variant="outline" size="sm" onClick={() => refetchTeams()}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={teamSearch}
                  onChange={e => setTeamSearch(e.target.value)}
                  placeholder="Search by team number or member name..."
                  className="pl-9"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={teamStatusFilter}
                  onChange={e => setTeamStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="eliminated">Eliminated</option>
                  <option value="scratched">Scratched</option>
                </select>
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={teamTypeFilter}
                  onChange={e => setTeamTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="picked">Pick</option>
                  <option value="random">Random</option>
                </select>
              </div>

              {teamsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : sortedTeams.length === 0 ? (
                <EmptyState icon={Users} title="No teams" description="Teams will appear here after the draw is executed." />
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="cursor-pointer select-none w-20" onClick={() => toggleSort('team_number')}>
                          <span className="flex items-center">Team # <SortIcon field="team_number" /></span>
                        </TableHead>
                        <TableHead className="w-20">Type</TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('header')}>
                          <span className="flex items-center">Header <SortIcon field="header" /></span>
                        </TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('heeler')}>
                          <span className="flex items-center">Heeler <SortIcon field="heeler" /></span>
                        </TableHead>
                        <TableHead className="text-right w-28">Combined HC</TableHead>
                        <TableHead className="text-right w-28">Combined Age</TableHead>
                        <TableHead className="cursor-pointer select-none w-24" onClick={() => toggleSort('status')}>
                          <span className="flex items-center">Status <SortIcon field="status" /></span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedTeams.map((team: any) => {
                        const names = team.member_names ?? [];
                        const handicaps = team.member_handicaps ?? [];
                        void team.member_cities;
                        void team.member_states;
                        const hHcp = handicaps[0] != null ? Number(handicaps[0]) : null;
                        const eHcp = handicaps[1] != null ? Number(handicaps[1]) : null;
                        const combinedAge = (team.header_age ?? 0) + (team.heeler_age ?? 0);
                        const isCentury = combinedAge >= 100;
                        const typeLabel = team.team_type === 'picked'
                          ? `Pick ${team.pick_round ?? 1}`
                          : team.team_type === 'random' ? 'Random' : team.team_type ?? '—';
                        const typeColor = team.team_type === 'picked'
                          ? 'bg-blue-500/15 text-blue-500 border-blue-500/30'
                          : 'bg-gray-500/15 text-gray-400 border-gray-500/30';
                        return (
                          <TableRow key={team.id}>
                            <TableCell className="font-mono font-bold">#{team.team_number}</TableCell>
                            <TableCell>
                              <Badge className={`text-xs ${typeColor}`}>{typeLabel}</Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium text-sm">{names[0] ?? '—'}</span>
                                {team.header_gender && <span className="text-xs text-muted-foreground ml-1">({team.header_gender?.[0]?.toUpperCase()}{team.header_age ? `, ${team.header_age}` : ''})</span>}
                              </div>
                              {hHcp != null && <div className="text-xs text-muted-foreground">HC: {hHcp.toFixed(1)}</div>}
                            </TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium text-sm">{names[1] ?? '—'}</span>
                                {team.heeler_gender && <span className="text-xs text-muted-foreground ml-1">({team.heeler_gender?.[0]?.toUpperCase()}{team.heeler_age ? `, ${team.heeler_age}` : ''})</span>}
                              </div>
                              {eHcp != null && <div className="text-xs text-muted-foreground">HC: {eHcp.toFixed(1)}</div>}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {team.combined_handicap != null ? Number(team.combined_handicap).toFixed(1) : '—'}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm ${isCentury && combinedAge > 0 ? 'text-red-500 font-bold' : ''}`}>
                              {combinedAge > 0 ? combinedAge : '—'}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={team.status ?? 'active'} variant="event" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* ─── Draw Order Tab ────────────────────────────────────── */}
          {activeTab === 'draw-order' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium">Draw Order</h3>
                  <p className="text-sm text-muted-foreground">
                    Drag and drop teams to reorder them, or use the reshuffle button to automatically space contestants 10+ runs apart.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {drawOrderDirty && (
                    <Button size="sm" onClick={saveDrawOrder} disabled={savingOrder}>
                      {savingOrder ? 'Saving...' : 'Save Order'}
                    </Button>
                  )}
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" disabled>
                    <Shuffle className="h-4 w-4 mr-1" /> Reshuffle with Spacing
                  </Button>
                </div>
              </div>

              {teamsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : drawOrder.length === 0 ? (
                <EmptyState icon={ArrowUpDown} title="No draw order" description="Execute the draw to generate the draw order." />
              ) : (
                <div className="space-y-1">
                  {drawOrder.map((team: any, idx: number) => {
                    const names = team.member_names ?? [];
                    const typeLabel = team.team_type === 'picked'
                      ? `Pick ${team.pick_round ?? 1}`
                      : team.team_type === 'random' ? 'Random' : team.team_type ?? '—';
                    const typeColor = team.team_type === 'picked'
                      ? 'bg-blue-500/15 text-blue-500 border-blue-500/30'
                      : 'bg-gray-500/15 text-gray-400 border-gray-500/30';
                    const isDragging = dragIdx === idx;
                    const isOver = dragOverIdx === idx;
                    return (
                      <div
                        key={team.id}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={() => handleDrop(idx)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-4 rounded-lg border p-3 transition-all select-none
                          ${isDragging ? 'opacity-40 scale-95' : ''}
                          ${isOver ? 'border-blue-500 bg-blue-500/5' : 'hover:bg-muted/50'}
                        `}
                      >
                        <div className="flex items-center gap-1 text-muted-foreground cursor-grab active:cursor-grabbing">
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                        <div className="text-2xl font-bold w-12">#{idx + 1}</div>
                        <div className="text-xs text-muted-foreground w-16">Team #{team.team_number}</div>
                        <Badge className={`text-xs ${typeColor}`}>{typeLabel}</Badge>
                        <div className="flex-1 font-medium text-sm">{names[0] ?? 'Unknown'} &amp; {names[1] ?? 'Unknown'}</div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
                          HC: {team.combined_handicap != null ? Number(team.combined_handicap).toFixed(1) : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ─── Contestants Tab ──────────────────────────────────── */}
          {activeTab === 'contestants' && (
            <ContestantsList eventId={eventId} />
          )}
        </div>
      </div>

      {/* Create Teams Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Teams</DialogTitle>
            <DialogDescription>{eventName}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Current Status */}
            <div>
              <p className="text-sm text-muted-foreground">Current Status</p>
              <p className={`text-sm font-medium ${isExecuted ? 'text-green-500' : 'text-amber-500'}`}>
                {isExecuted ? 'Teams Created' : 'Teams Not Created'}
              </p>
            </div>

            {/* Event Configuration */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Event Configuration</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pick-rounds">Pick Rounds</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="pick-rounds"
                      type="number"
                      min={0}
                      max={10}
                      value={createPickRounds}
                      onChange={e => setCreatePickRounds(parseInt(e.target.value) || 0)}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">round{createPickRounds !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="draw-rounds">Draw Rounds</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="draw-rounds"
                      type="number"
                      min={0}
                      max={10}
                      value={createDrawRounds}
                      onChange={e => setCreateDrawRounds(parseInt(e.target.value) || 0)}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">round{createDrawRounds !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm space-y-0.5">
                <div>Total Team Rounds: <strong>{createPickRounds + createDrawRounds} runs</strong></div>
                {eventTotalRounds > 0 && (
                  <div className="text-xs text-muted-foreground">Event has {eventTotalRounds} round{eventTotalRounds !== 1 ? 's' : ''} configured</div>
                )}
              </div>
            </div>

            {/* Draw Algorithm */}
            <div className="space-y-1.5">
              <Label>Draw Algorithm</Label>
              <Select value={createAlgorithm} onValueChange={setCreateAlgorithm}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random_balanced">Random Balanced</SelectItem>
                  <SelectItem value="handicap_balanced">Handicap Balanced</SelectItem>
                  <SelectItem value="pure_random">Pure Random</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Advanced Options */}
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-500 hover:text-blue-400 flex items-center gap-1"
            >
              <Settings2 className="h-3.5 w-3.5" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </button>
            {showAdvanced && (
              <div className="rounded-md border p-3 space-y-2 text-sm text-muted-foreground">
                <p>Advanced options like handicap limits, century/mixed rules, and slide types are configured on the event settings. Edit the event to change these.</p>
              </div>
            )}

            {/* Error display */}
            {(executeDraw.isError || updateDrawConfig.isError) && (
              <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
                {(executeDraw.error as any)?.message ?? (updateDrawConfig.error as any)?.message ?? 'Failed to create teams'}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button
              onClick={handleCreateTeams}
              disabled={isPending || (createPickRounds === 0 && createDrawRounds === 0)}
            >
              {isPending ? 'Creating...' : 'Create Teams'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog (rollback, lock, unlock) */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction ? confirmMessages[confirmAction]?.title : ''}
            </DialogTitle>
            <DialogDescription>
              {confirmAction ? confirmMessages[confirmAction]?.desc : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              variant={confirmAction && confirmMessages[confirmAction]?.destructive ? 'destructive' : 'default'}
              onClick={handleConfirm}
              disabled={isPending}
            >
              {isPending ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Contestants List (inline) ───────────────────────────────────────────────

function ContestantsList({ eventId }: { eventId: string }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contestants', eventId],
    queryFn: () => httpClient.get<any>(`/registration-ops/event/${eventId}/registrations`),
    enabled: !!eventId,
  });

  const registrations = (data as any)?.registrations ?? (data as any)?.data ?? (Array.isArray(data) ? data : []);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // Admin-only entry point for the committee manual-entry flow. Delegates
  // can still see contestants but cannot create rows on someone else's
  // behalf — that's locked to admin role server-side too.
  const isAdmin = useAuthStore((s) => s.isAdmin)();

  const filtered = useMemo(() => {
    let list = registrations;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r: any) =>
        (r.trig_first_name ?? r.first_name ?? '').toLowerCase().includes(q) ||
        (r.trig_last_name ?? r.last_name ?? '').toLowerCase().includes(q) ||
        (r.trig_display_name ?? r.display_name ?? '').toLowerCase().includes(q) ||
        (r.trig_user_email ?? '').toLowerCase().includes(q) ||
        (r.position ?? '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      list = list.filter((r: any) => (r.trig_status_name ?? r.trig_status ?? '').toLowerCase() === statusFilter);
    }
    if (partnerFilter === 'with') {
      list = list.filter((r: any) => r.partner_user_profile_id);
    } else if (partnerFilter === 'without') {
      list = list.filter((r: any) => !r.partner_user_profile_id);
    }
    return list;
  }, [registrations, search, statusFilter, partnerFilter]);

  // Stats
  const total = registrations.length;
  const confirmed = registrations.filter((r: any) => (r.trig_status_name ?? '').toLowerCase() === 'confirmed').length;
  const withPartner = registrations.filter((r: any) => r.partner_user_profile_id).length;
  const pendingPartner = registrations.filter((r: any) => r.partner_status === 'pending').length;
  const noPartner = registrations.filter((r: any) => !r.partner_user_profile_id && r.registration_type === 'individual').length;
  const avgHandicap = total > 0
    ? registrations.reduce((sum: number, r: any) => {
        const hcp = r.position === 'Header' ? Number(r.trig_header_handicap_value) : Number(r.trig_heeler_handicap_value);
        return sum + (isNaN(hcp) ? 0 : hcp);
      }, 0) / total
    : 0;
  const males = registrations.filter((r: any) => (r.trig_gender ?? r.gender ?? '').toLowerCase() === 'male').length;
  const females = registrations.filter((r: any) => (r.trig_gender ?? r.gender ?? '').toLowerCase() === 'female').length;
  const headers = registrations.filter((r: any) => r.position === 'Header').length;
  const heelers = registrations.filter((r: any) => r.position === 'Heeler').length;

  if (isLoading) {
    return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Entered Contestants</h3>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setAddDialogOpen(true)}
            >
              <UserPlus className="h-4 w-4 mr-1" /> Add Contestant
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {isAdmin && (
        <AdminAddContestantDialog
          eventId={eventId}
          open={addDialogOpen}
          onOpenChange={(open) => {
            setAddDialogOpen(open);
            // Refetch when closing so a successful add shows up immediately.
            if (!open) refetch();
          }}
        />
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {([
          ['Total', total, ''],
          ['Confirmed', confirmed, 'text-green-500'],
          ['With Partner', withPartner, 'text-blue-500'],
          ['Pending Partner', pendingPartner, 'text-amber-500'],
          ['No Partner', noPartner, 'text-muted-foreground'],
          ['Avg Handicap', avgHandicap.toFixed(1), 'text-purple-500'],
        ] as [string, string | number, string][]).map(([label, val, color]) => (
          <div key={label} className="text-center">
            <div className={`text-2xl font-bold ${color}`}>{val}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>

      {/* Distribution Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Gender Distribution</div>
          <div className="flex gap-4">
            <div><span className="text-lg font-bold text-blue-500">{males}</span> <span className="text-xs text-muted-foreground">Male{total > 0 ? ` ${Math.round(males/total*100)}%` : ''}</span></div>
            <div><span className="text-lg font-bold text-pink-500">{females}</span> <span className="text-xs text-muted-foreground">Female{total > 0 ? ` ${Math.round(females/total*100)}%` : ''}</span></div>
          </div>
        </div>
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Position Distribution</div>
          <div className="flex gap-4">
            <div><span className="text-lg font-bold text-blue-600">{headers}</span> <span className="text-xs text-muted-foreground">Headers{total > 0 ? ` ${Math.round(headers/total*100)}%` : ''}</span></div>
            <div><span className="text-lg font-bold text-purple-500">{heelers}</span> <span className="text-xs text-muted-foreground">Heelers{total > 0 ? ` ${Math.round(heelers/total*100)}%` : ''}</span></div>
          </div>
        </div>
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Age Distribution</div>
          <div className="flex gap-3 text-xs">
            {(() => {
              let a025 = 0, a2550 = 0, a5075 = 0, a75 = 0;
              for (const r of registrations) {
                const dob = r.date_of_birth ?? r.trig_date_of_birth;
                if (!dob) continue;
                const age = Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000);
                if (age < 25) a025++; else if (age < 50) a2550++; else if (age < 75) a5075++; else a75++;
              }
              return (
                <>
                  <div><span className="text-lg font-bold text-blue-400">{a025}</span> <span className="text-muted-foreground">0-25</span></div>
                  <div><span className="text-lg font-bold text-green-500">{a2550}</span> <span className="text-muted-foreground">25-50</span></div>
                  <div><span className="text-lg font-bold text-amber-500">{a5075}</span> <span className="text-muted-foreground">50-75</span></div>
                  <div><span className="text-lg font-bold text-red-500">{a75}</span> <span className="text-muted-foreground">75+</span></div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, location..."
            className="pl-9"
          />
        </div>
        <div className="flex gap-3">
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="pending_payment">Pending Payment</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={partnerFilter}
            onChange={e => setPartnerFilter(e.target.value)}
          >
            <option value="">All Partnerships</option>
            <option value="with">With Partner</option>
            <option value="without">Without Partner</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState icon={User} title="No contestants" description="No entered contestants found for this event." />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contestant</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Handicap</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Entered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r: any) => {
                const firstName = r.trig_first_name ?? r.first_name ?? '';
                const lastName = r.trig_last_name ?? r.last_name ?? '';
                const name = `${firstName} ${lastName}`.trim() || '—';
                const gender = r.trig_gender ?? r.gender ?? '';
                const dob = r.date_of_birth ?? r.trig_date_of_birth;
                const age = dob ? Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000) : null;
                const handicap = r.position === 'Header'
                  ? r.trig_header_handicap_value
                  : r.trig_heeler_handicap_value;
                const partnerReg = r.partner_user_profile_id
                  ? registrations.find((p: any) => p.user_profile_id === r.partner_user_profile_id)
                  : null;
                const partnerName = r.partner_name ?? r.trig_partner_name ?? '';
                const partnerGender = partnerReg?.gender ?? partnerReg?.trig_gender ?? '';
                const partnerDob = partnerReg?.date_of_birth ?? partnerReg?.trig_date_of_birth;
                const partnerAge = partnerDob ? Math.floor((Date.now() - new Date(partnerDob).getTime()) / 31557600000) : null;
                const email = r.trig_user_email ?? '';
                const phone = r.trig_user_phone ?? r.phone ?? '';
                const status = r.trig_status_name ?? r.trig_status ?? 'pending';
                const regDate = r.created_at ? new Date(r.created_at).toLocaleDateString() : '—';
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium text-sm">{name}</span>
                        {(gender || age) && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({gender ? gender[0].toUpperCase() : ''}{age != null ? `, ${age}` : ''})
                          </span>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-[10px] mt-0.5">{r.position ?? '—'}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {email && <div>{email}</div>}
                      {phone && <div>{phone}</div>}
                      {!email && !phone && '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {handicap != null ? Number(handicap).toFixed(1) : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {partnerName ? (
                        <span>
                          <span className="text-blue-500">{partnerName}</span>
                          {(partnerGender || partnerAge != null) && (
                            <span className="text-xs text-muted-foreground ml-1">
                              ({partnerGender ? partnerGender[0].toUpperCase() : ''}{partnerAge != null ? `, ${partnerAge}` : ''})
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={status} variant="registration" />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{regDate}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ─── Error Boundary for Draw Management ──────────────────────────────────────

import { Component, type ErrorInfo, type ReactNode } from 'react';

class DrawErrorBoundary extends Component<{ children: ReactNode; onBack: () => void }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('DrawManagement crash:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="space-y-4 p-6">
          <h2 className="text-xl font-bold text-red-500">Draw Management Error</h2>
          <p className="text-sm text-muted-foreground">Something went wrong loading the draw management view.</p>
          <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-48">{this.state.error.message}{'\n'}{this.state.error.stack}</pre>
          <Button variant="outline" onClick={() => { this.setState({ error: null }); this.props.onBack(); }}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Manage Draws
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Manage Draws Helpers ────────────────────────────────────────────────────

// Small helper: friendly label + color tone per placement_status. Kept
// inline because nothing else in the app surfaces these as a unified
// badge; if a second consumer appears we'll lift it into status-badge.tsx.
function DrawStateBadge({
  status,
  isCapacityConfigured,
}: {
  status: DrawPlacementStatus | undefined;
  isCapacityConfigured?: boolean;
}) {
  // `none` + no capacity = the delegate has done nothing yet → use a
  // distinct muted "Not started" label so admins can scan for it.
  const effective: DrawPlacementStatus | 'not_started' = !status || status === 'none'
    ? (isCapacityConfigured ? 'none' : 'not_started')
    : status;

  if (effective === 'finalized') {
    return (
      <Badge className="text-[10px] uppercase tracking-wide bg-indigo-600 hover:bg-indigo-700 text-white">
        <CheckCircle2 className="h-3 w-3 mr-1" /> Finalized
      </Badge>
    );
  }
  if (effective === 'published') {
    return (
      <Badge className="text-[10px] uppercase tracking-wide bg-green-600 hover:bg-green-700 text-white">
        <Lock className="h-3 w-3 mr-1" /> Locked
      </Badge>
    );
  }
  if (effective === 'draft') {
    return (
      <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
        <Shuffle className="h-3 w-3 mr-1" /> Draft
      </Badge>
    );
  }
  if (effective === 'none') {
    return (
      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
        Capacity set · No draw yet
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-wide text-muted-foreground"
    >
      Not started
    </Badge>
  );
}

function formatRelativeTimestamp(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Upcoming / Past draw filtering ──────────────────────────────────────────
// The finished-check + sort comparator live in @/utils/scheduleTabs so the
// Live Scoring screen reuses the exact same logic. `DrawTimeTab` is kept as a
// local alias to minimize churn in this file.

type DrawTimeTab = ScheduleTab;
const isPastDraw = isPastSchedule;
const sortDrawsByTab = sortScheduleByTab;

function ManageDrawsEventRow({
  event,
  status,
  onManage,
}: {
  event: any;
  status: EventDrawStatus | undefined;
  onManage: () => void;
}) {
  const setResultsVisibility = useSetDrawResultsVisibility();
  const entered = status?.entered_count ?? 0;
  const cancelled = status?.cancelled_count ?? 0;
  const placed = status?.placed_count ?? 0;
  const notDrawn = status?.not_drawn_count ?? 0;
  const unpaid = status?.unpaid_count ?? 0;
  // Pick the most-recent lifecycle timestamp to show as "last action".
  // Falls back through finalized → published → executed so the cell is
  // always populated once the draw has done anything.
  const lastActionIso =
    status?.finalized_at ??
    status?.published_at ??
    status?.executed_at ??
    null;
  const lastActionLabel = formatRelativeTimestamp(lastActionIso);
  const lastActionVerb =
    status?.placement_status === 'finalized'
      ? 'finalized'
      : status?.placement_status === 'published'
        ? 'published'
        : status?.placement_status === 'draft'
          ? 'drafted'
          : null;

  // "X of Y entered" reads more clearly when there were cancellations —
  // it signals to the admin that the lower drawn count is expected (some
  // entries were refunded, not lost). Total = active + cancelled mirrors
  // the count the contestants originally registered as.
  const totalEverEntered = entered + cancelled;

  // Two ancillary badges that mirror the DrawStateBadge / Finalize
  // pattern. Both source from the same batched event-statuses payload so
  // there's no extra round-trip per row.
  //
  //   - "Notifications sent": notifications_sent_at is populated on
  //     draw_executions whenever a send batch lands. Surfaces purely the
  //     fact that contestants have been notified — admins can drill in
  //     for per-send detail.
  //   - "Live / showing": events.draw_results_visible is the canonical
  //     "the public draw-results card is up on the event page right now"
  //     column. Decoupled from finalized state because the admin toggles
  //     it explicitly after finalize (and the unfinalize path
  //     force-clears it, so it always reflects current truth).
  const notificationsSent = !!status?.notifications_sent_at;
  // The per-event draw lifecycle drives two visibility rules below:
  //   - the "Draft event" (unpublished-event) badge clears once the draw
  //     is locked or finalized, mirroring the draw-detail page where the
  //     header badge stops reading "Draft" at that point. Previously this
  //     badge keyed only off `is_published` so it never went away.
  //   - the "Show results" toggle only appears once the draw is finalized
  //     (results are gated on finalize state server-side).
  const drawLockedOrFinalized =
    status?.placement_status === 'published' ||
    status?.placement_status === 'finalized';
  const isFinalized = status?.placement_status === 'finalized';

  return (
    <div className="px-6 py-3 hover:bg-muted/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium truncate">
              {getEventDisplayTitle(event)}
            </h3>
            {!event.is_published && !drawLockedOrFinalized && (
              <Badge variant="secondary" className="text-xs">
                Draft event
              </Badge>
            )}
            <DrawStateBadge
              status={status?.placement_status}
              isCapacityConfigured={status?.has_capacity}
            />
            {notificationsSent && (
              <Badge
                title={
                  status?.notifications_sent_at
                    ? `Notifications last sent ${new Date(status.notifications_sent_at).toLocaleString()}${
                        status?.notifications_sent_count
                          ? ` to ${status.notifications_sent_count} recipient${status.notifications_sent_count === 1 ? '' : 's'}`
                          : ''
                      }.`
                    : 'Notifications have been sent at least once.'
                }
                className="text-[10px] uppercase tracking-wide bg-sky-600 hover:bg-sky-700 text-white"
              >
                <Send className="h-3 w-3 mr-1" /> Notifications sent
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1 text-blue-500 font-medium">
              <Users className="h-3 w-3" />
              {cancelled > 0
                ? `${entered} of ${totalEverEntered} entered`
                : `${entered} entered`}
            </span>
            {placed > 0 && (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" />
                {placed} drawn
              </span>
            )}
            {notDrawn > 0 && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                <AlertTriangle className="h-3 w-3" />
                {notDrawn} not drawn
              </span>
            )}
            {cancelled > 0 && (
              <span className="flex items-center gap-1 text-rose-500">
                <Ban className="h-3 w-3" />
                {cancelled} cancelled
              </span>
            )}
            {unpaid > 0 && (
              <span className="flex items-center gap-1 text-red-500">
                <AlertTriangle className="h-3 w-3" />
                {unpaid} unpaid
              </span>
            )}
            {lastActionLabel && lastActionVerb && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {lastActionVerb} {lastActionLabel}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Per-event "Show results" toggle — lets the delegate publish the
              draw-results card to contestants straight from the list without
              opening the draw. Only appears once the draw is finalized
              (results are gated on finalize state); the same toggle remains on
              the draw-detail page and both stay in sync via the shared query
              cache. */}
          {isFinalized && (
            <div className="flex items-center gap-1.5">
              <Switch
                id={`show-results-${event.id}`}
                checked={!!status?.results_visible}
                onCheckedChange={(visible) =>
                  setResultsVisibility.mutate({ eventId: event.id, visible })
                }
                disabled={setResultsVisibility.isPending}
              />
              <label
                htmlFor={`show-results-${event.id}`}
                className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
              >
                Show results
              </label>
            </div>
          )}
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={onManage}
          >
            <Shuffle className="h-3 w-3 mr-1.5" />
            Manage Draw
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Rodeo-header master toggle: flips the "show results to contestants"
// state for EVERY finalized event in the rodeo at once. Hidden entirely
// until at least one event is finalized (results can't be shown before
// then). There's no bulk endpoint, so it fans out per-event calls; the
// shared draw-list-status cache invalidation in the mutation keeps every
// per-row toggle in sync afterward. Reflects "all on" as checked and
// surfaces a count so a mixed state is still legible.
function RodeoShowAllResultsToggle({
  events,
  statuses,
}: {
  events: any[];
  statuses: Record<string, EventDrawStatus | undefined>;
}) {
  const setResultsVisibility = useSetDrawResultsVisibility();
  const finalizedEvents = events.filter(
    (ev) => statuses[ev.id]?.placement_status === 'finalized',
  );
  if (finalizedEvents.length === 0) return null;

  const shownCount = finalizedEvents.filter(
    (ev) => statuses[ev.id]?.results_visible,
  ).length;
  const allShown = shownCount === finalizedEvents.length;

  const handleToggleAll = (next: boolean) => {
    finalizedEvents.forEach((ev) => {
      if (!!statuses[ev.id]?.results_visible !== next) {
        setResultsVisibility.mutate({ eventId: ev.id, visible: next });
      }
    });
  };

  return (
    <div
      className="flex items-center gap-1.5 mr-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <Switch
        id="show-all-results"
        checked={allShown}
        onCheckedChange={handleToggleAll}
        disabled={setResultsVisibility.isPending}
      />
      <label
        htmlFor="show-all-results"
        className="text-xs text-white/90 cursor-pointer whitespace-nowrap"
      >
        Show all results ({shownCount}/{finalizedEvents.length})
      </label>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// Persists the list view's expanded-rodeo set across the manage-view drill-in.
// The Manage Draw page lives at a separate route (/delegate/draws/:eventId), so
// React Router remounts this component on the way back — pure useState would
// reset every expansion. The session-scoped storage lives in the shared
// scheduleListState helper, which Live Scoring and Prize Management use too.
const drawsViewState = createScheduleListState('delegate-draws');
const loadExpandedRodeoIds = drawsViewState.loadExpandedIds;
const saveExpandedRodeoIds = drawsViewState.saveExpandedIds;

export default function DelegateDrawsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { eventId: eventIdParam } = useParams<{ eventId: string }>();
  const navState = (location.state as any) ?? {};
  // Nav-state is the optimistic fast path: when the user clicked Manage
  // Draw from the list we already know the event name + parent ID, so we
  // can render the manage view immediately without waiting for the event
  // fetch. On deep-link refresh (no nav-state) we fall back to fetching
  // the event below.
  const eventId = eventIdParam ?? null;
  const navStateEventName: string = navState.eventName ?? '';
  const navStateJackpotId: string | null = navState.jackpotId ?? null;
  const navStateRodeoId: string | null = navState.rodeoId ?? null;
  const navStateDrawKind: 'rodeo' | 'jackpot' | null = navState.drawKind ?? null;

  // Fetch the event when we're in manage view AND don't already have the
  // draw kind from nav-state (typically: deep-link refresh wipes
  // location.state). The event's rodeo_id points at the parent row in the
  // dual-purpose `rodeos` table; that row's `rodeo_or_jackpot`
  // discriminator tells us which manage component to render.
  const needsEventLookup = !!eventId && !navStateDrawKind;
  const {
    data: drawEvent,
    isLoading: drawEventLoading,
    isError: drawEventError,
  } = useEvents(needsEventLookup ? eventId! : '');
  const parentIdFromEvent = (drawEvent as any)?.rodeo_id ?? null;
  const { data: drawParent } = useRodeoDetail(
    needsEventLookup && parentIdFromEvent ? parentIdFromEvent : '',
  );
  const parentKindFromApi = (drawParent as any)?.rodeo_or_jackpot;

  // Resolved values: prefer nav-state (no extra round-trip on list-click),
  // fall back to the fetched event/parent on deep link.
  const resolvedDrawKind: 'rodeo' | 'jackpot' =
    navStateDrawKind ?? (parentKindFromApi === 'jackpot' ? 'jackpot' : 'rodeo');
  const resolvedEventName: string =
    navStateEventName || getEventDisplayTitle(drawEvent as any) || '';
  const resolvedJackpotId: string | null =
    navStateJackpotId ?? (resolvedDrawKind === 'jackpot' ? parentIdFromEvent : null);
  const resolvedRodeoId: string | null =
    navStateRodeoId ?? (resolvedDrawKind === 'rodeo' ? parentIdFromEvent : null);

  // Breadcrumb trail: "Manage Draws > <event name>" when drilled in.
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!eventId) {
      setTrail(null);
      return;
    }
    setTrail([
      { label: 'Manage Draws', path: '/delegate/draws' },
      // Until the event name resolves (deep-link refresh), keep the
      // trailing crumb generic rather than flashing the raw UUID.
      { label: resolvedEventName || 'Event', path: `/delegate/draws/${eventId}` },
    ]);
    return () => setTrail(null);
  }, [eventId, resolvedEventName, setTrail]);

  // Set of rodeo IDs the user has expanded on the list view. Persisted
  // in sessionStorage so navigating into manage view (a different route
  // that remounts this component) and clicking Back doesn't collapse
  // every card. A full browser refresh also wipes session storage so
  // behavior matches the previous in-memory model.
  const [expandedRodeoIds, setExpandedRodeoIds] = useState<Set<string>>(
    () => loadExpandedRodeoIds(),
  );
  const toggleRodeoExpanded = (rodeoIdToToggle: string, next: boolean) => {
    setExpandedRodeoIds((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(rodeoIdToToggle);
      else updated.delete(rodeoIdToToggle);
      saveExpandedRodeoIds(updated);
      return updated;
    });
  };
  const { data: jackpotsList, isLoading: jackpotsLoading } = useJackpotsList({
    pageSize: 100,
  });
  // Rodeos are the live, in-scope flow. Jackpots stay listed but route to
  // the legacy team-pairing screen untouched (no live jackpots today).
  const { data: rodeosList, isLoading: rodeosLoading } = useRodeosList({
    page_size: 100,
  });

  const jackpots = (jackpotsList as any)?.data ?? [];
  const rodeos = (rodeosList as any)?.data ?? [];

  // Upcoming vs past tab. The Manage Draws list defaults to Upcoming so
  // delegates only see draws that still need work; the Past tab keeps
  // completed events reachable for reference.
  const [drawTab, setDrawTab] = useState<DrawTimeTab>('upcoming');

  const drawCounts = useMemo(() => {
    let upcoming = 0;
    let past = 0;
    for (const item of [...rodeos, ...jackpots]) {
      if (isPastDraw(item)) past += 1;
      else upcoming += 1;
    }
    return { upcoming, past };
  }, [rodeos, jackpots]);

  const visibleRodeos = useMemo(
    () =>
      sortDrawsByTab(
        rodeos.filter((r: any) =>
          drawTab === 'past' ? isPastDraw(r) : !isPastDraw(r),
        ),
        drawTab,
      ),
    [rodeos, drawTab],
  );

  const visibleJackpots = useMemo(
    () =>
      sortDrawsByTab(
        jackpots.filter((j: any) =>
          drawTab === 'past' ? isPastDraw(j) : !isPastDraw(j),
        ),
        drawTab,
      ),
    [jackpots, drawTab],
  );

  // Batched per-event draw status (placement state + counts) for every
  // event under every rodeo/jackpot on the page. One round-trip replaces
  // the previous N+1 `/event-registrations/count` loop AND surfaces the
  // draw lifecycle state (none/draft/published/finalized), drawn-out
  // count, unpaid count, and last-action timestamps that the admin needs
  // to triage from the list without drilling in.
  const allEventIds = useMemo<string[]>(() => {
    const ids: string[] = [];
    for (const r of rodeos) {
      for (const ev of r.events ?? []) {
        if (ev?.id) ids.push(ev.id);
      }
    }
    for (const j of jackpots) {
      for (const ev of j.events ?? []) {
        if (ev?.id) ids.push(ev.id);
      }
    }
    return ids;
  }, [rodeos, jackpots]);
  const { data: statusesData } = useEventDrawStatuses(allEventIds);
  const statuses = statusesData?.statuses ?? {};

  // Entry counts derived from the batched statuses — kept around for the
  // jackpot rows that already use `regCounts` so the existing render path
  // still compiles. New rodeo rows use `statuses[ev.id]` directly so they
  // can light up additional state.
  const regCounts: RegistrationCounts = useMemo(() => {
    const out: RegistrationCounts = {};
    for (const id of allEventIds) {
      out[id] = statuses[id]?.entered_count ?? 0;
    }
    return out;
  }, [allEventIds, statuses]);

  // Manage Draw click: deep-link to the routed manage view and pass the
  // already-known context (event name + parent id + draw kind) via
  // location.state so the manage view renders without any extra fetch on
  // the optimistic path. Deep-link refresh on /delegate/draws/:eventId
  // falls back to the useEvents/useRodeoDetail lookup above.
  const handleManageDraw = (evId: string, evName: string, jpId: string) => {
    navigate(`/delegate/draws/${evId}`, {
      state: { eventName: evName, jackpotId: jpId, drawKind: 'jackpot' },
    });
  };

  const handleManageRodeoDraw = (evId: string, evName: string, rId: string) => {
    navigate(`/delegate/draws/${evId}`, {
      state: { eventName: evName, rodeoId: rId, drawKind: 'rodeo' },
    });
  };

  const handleBack = () => {
    navigate('/delegate/draws');
  };

  // ─── Draw management view ────────────────────────────────────────────
  if (eventId) {
    // Deep-link refresh: we need the event lookup AND the parent lookup
    // to resolve before we know which manage component to render. Show a
    // skeleton instead of flashing the wrong one (which would unmount as
    // soon as the parent discriminator resolves and lose any in-flight
    // state).
    const drawKindNotYetKnown =
      needsEventLookup &&
      (drawEventLoading ||
        (!!drawEvent && !!parentIdFromEvent && parentKindFromApi === undefined));
    if (drawKindNotYetKnown) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
      );
    }

    // Event lookup failed (deleted / unauthorized / bad id). Render a
    // not-found state with a way back to the list rather than crashing
    // the manage component, which assumes a valid event.
    if (needsEventLookup && (drawEventError || (!drawEventLoading && !drawEvent))) {
      return (
        <div className="space-y-4">
          <button
            onClick={handleBack}
            className="text-blue-500 hover:text-blue-400 text-sm flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to Manage Draws
          </button>
          <EmptyState
            icon={AlertTriangle}
            title="Draw not found"
            description="This event draw doesn't exist or you don't have permission to view it."
          />
        </div>
      );
    }

    if (resolvedDrawKind === 'rodeo') {
      return (
        <DrawErrorBoundary onBack={handleBack}>
          <RodeoDrawManagement
            eventId={eventId}
            eventName={resolvedEventName}
            rodeoId={resolvedRodeoId}
            onBack={handleBack}
          />
        </DrawErrorBoundary>
      );
    }

    return (
      <DrawErrorBoundary onBack={handleBack}>
        <DrawManagement
          eventId={eventId}
          eventName={resolvedEventName}
          jackpotId={resolvedJackpotId}
          onBack={handleBack}
        />
      </DrawErrorBoundary>
    );
  }

  // ─── Rodeo / jackpot list view ───────────────────────────────────────
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Manage Draws</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Select an event to manage its draw
        </p>
      </div>

      {/* Upcoming / Past tabs — switch between draws still ahead and
          completed ones. Upcoming is the default so only relevant work shows. */}
      <div className="flex items-center gap-1 border-b border-border">
        {(
          [
            ['upcoming', 'Upcoming', drawCounts.upcoming],
            ['past', 'Past', drawCounts.past],
          ] as [DrawTimeTab, string, number][]
        ).map(([id, label, count]) => (
          <button
            key={id}
            onClick={() => setDrawTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              drawTab === id
                ? 'border-blue-500 text-blue-500'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-400'
            }`}
          >
            {label} Draws ({count})
          </button>
        ))}
      </div>

      {/* Rodeos — the live, ferris-wheel draw flow */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Rodeos
        </h2>
        {rodeosLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : visibleRodeos.length === 0 ? (
          <div className="rounded-lg border border-border bg-muted/50 p-8 text-center">
            <Shuffle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              {drawTab === 'past'
                ? 'No past rodeos'
                : 'No upcoming rodeos'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {visibleRodeos.map((rodeo: any) => {
              // Sort events alphabetically by display title within each
              // rodeo so the Manage Draws list is scannable rather than
              // ordered by whatever the API happened to return.
              const events = [...(rodeo.events ?? [])].sort((a: any, b: any) =>
                getEventDisplayTitle(a).localeCompare(
                  getEventDisplayTitle(b),
                  undefined,
                  { sensitivity: 'base' },
                ),
              );
              if (events.length === 0) return null;
              const activeEvents = events.filter((e: any) => e.is_published);
              // Rodeo-level rollup: sum the per-event entered, not-drawn,
              // and cancelled counts so the admin sees aggregate
              // participation and outstanding work without expanding any
              // rows. not_drawn is the one that needs admin follow-up;
              // cancelled is informational (already refunded).
              let rodeoEnteredTotal = 0;
              let rodeoNotDrawnTotal = 0;
              let rodeoCancelledTotal = 0;
              // Drives the bulk reset / reset-and-run header actions —
              // both buttons are disabled when zero draft events exist
              // (server-side bulk endpoints only touch DRAFT events anyway,
              // but the FE disables to avoid an empty-modal round-trip).
              let rodeoDraftEventCount = 0;
              for (const ev of events) {
                const st = statuses[ev.id];
                if (st) {
                  rodeoEnteredTotal += st.entered_count;
                  rodeoNotDrawnTotal += st.not_drawn_count;
                  rodeoCancelledTotal += st.cancelled_count;
                  if (st.placement_status === 'draft') {
                    rodeoDraftEventCount += 1;
                  }
                }
              }
              const expandedPanels = (
                <>
                  {/* Buddy-group inspection panel — sits at the top of
                      the rodeo block so split-performance issues are
                      visible at a glance. */}
                  <RodeoBuddyGroupsPanel
                    rodeoId={rodeo.id}
                    onOpenEvent={(evId) => {
                      const match = events.find((e: any) => e.id === evId);
                      handleManageRodeoDraw(
                        evId,
                        match ? getEventDisplayTitle(match) : 'Event',
                        rodeo.id,
                      );
                    }}
                  />
                  <RodeoLevelConflictsPanel
                    rodeoId={rodeo.id}
                    resolveEventTitle={(evId) => {
                      const match = events.find((e: any) => e.id === evId);
                      return match ? getEventDisplayTitle(match) : undefined;
                    }}
                    onOpenEvent={(evId) => {
                      const match = events.find((e: any) => e.id === evId);
                      handleManageRodeoDraw(
                        evId,
                        match ? getEventDisplayTitle(match) : 'Event',
                        rodeo.id,
                      );
                    }}
                  />
                </>
              );

              const eventRows = events.map((ev: any) => (
                <ManageDrawsEventRow
                  key={ev.id}
                  event={ev}
                  status={statuses[ev.id]}
                  onManage={() =>
                    handleManageRodeoDraw(
                      ev.id,
                      getEventDisplayTitle(ev),
                      rodeo.id,
                    )
                  }
                />
              ));

              return (
                <ManageDrawsRodeoCard
                  key={rodeo.id}
                  rodeo={rodeo}
                  events={events}
                  activeEventCount={activeEvents.length}
                  enteredTotal={rodeoEnteredTotal}
                  notDrawnTotal={rodeoNotDrawnTotal}
                  cancelledTotal={rodeoCancelledTotal}
                  draftEventCount={rodeoDraftEventCount}
                  statuses={statuses}
                  bulkActions={
                    <RodeoBulkDraftActions
                      rodeoId={rodeo.id}
                      draftEventCount={rodeoDraftEventCount}
                    />
                  }
                  masterResultsToggle={
                    <RodeoShowAllResultsToggle
                      events={events}
                      statuses={statuses}
                    />
                  }
                  expandedPanels={expandedPanels}
                  eventRows={eventRows}
                  expanded={expandedRodeoIds.has(rodeo.id)}
                  onExpandedChange={(next) =>
                    toggleRodeoExpanded(rodeo.id, next)
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Jackpots — legacy team-pairing flow, left as-is */}
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2">
        Jackpots
      </h2>
      {jackpotsLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : visibleJackpots.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/50 p-8 text-center">
          <Shuffle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {drawTab === 'past'
              ? 'No past jackpots'
              : 'No upcoming jackpots'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {visibleJackpots.map((jackpot: any) => {
            const events = jackpot.events ?? [];
            if (events.length === 0) return null;

            const activeEvents = events.filter((e: any) => e.is_published);

            return (
              <div
                key={jackpot.id}
                className="rounded-lg border border-border overflow-hidden"
              >
                {/* Jackpot Header */}
                <div className="bg-gray-700 dark:bg-gray-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-white">{jackpot.name}</h2>
                      {jackpot.primary_organization_name && (
                        <p className="text-sm text-gray-300">{jackpot.primary_organization_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-300">
                        {activeEvents.length} active event{activeEvents.length !== 1 ? 's' : ''} &bull; {events.length} total
                      </div>
                      {(jackpot.start_date || jackpot.end_date) && (
                        <div className="flex items-center justify-end gap-1 text-xs text-gray-400 mt-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDateRange(jackpot.start_date, jackpot.end_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Event Rows */}
                <div className="divide-y divide-border">
                  {events.map((ev: any) => (
                    <div
                      key={ev.id}
                      className="px-6 py-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-medium truncate">{getEventDisplayTitle(ev)}</h3>
                            {!ev.is_published && (
                              <Badge variant="secondary" className="text-xs">Draft</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1 text-blue-500 font-medium">
                              <Users className="h-3 w-3" />
                              {regCounts[ev.id] ?? 0} registered
                            </span>
                            <span>
                              {ev.pick_count ?? 0} picks, {ev.draw_count ?? 0} draws
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                          onClick={() => handleManageDraw(ev.id, getEventDisplayTitle(ev), jackpot.id)}
                        >
                          <Shuffle className="h-3 w-3 mr-1.5" />
                          Manage Draw
                          <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
